<section class="ai-section">
    <div class="container">
        <div class="card">
            <div class="page-header">
                <button class="icon-btn back" title="Back to conversations" (click)="back()"><i class="fa-solid fa-arrow-left"></i></button>
                <div>
                    <h3 class="page-title">Conversation</h3>
                    <p class="page-subtitle mono" *ngIf="conversation">{{ conversation.id }}</p>
                </div>
                <div class="flex-spacer"></div>
                <button *ngIf="conversation && perms.has(P.agentGet)" class="btn-light btn-inline" (click)="openAgent()">
                    <i class="fa-solid fa-robot"></i> Open agent
                </button>
            </div>

            <div *ngIf="error" class="error-text">{{ error }}</div>

            <div *ngIf="conversation" class="facts">
                <div><span class="muted">Agent</span><strong dir="auto">{{ agent?.name || conversation.agentId }}</strong></div>
                <div><span class="muted">Project</span><span class="mono">{{ conversation.projectId }}</span></div>
                <div><span class="muted">Channel</span><span class="badge">{{ channelLabel(conversation.channelType) }}</span></div>
                <div><span class="muted">Visitor</span><span class="mono">{{ conversation.externalUserKey || '-' }}</span></div>
                <div><span class="muted">Status</span><span class="badge" [ngClass]="conversation.status === 'OPEN' ? 'ok' : ''">{{ conversation.status }}</span></div>
                <div><span class="muted">Started</span>{{ conversation.startedAt | date: 'MMM d, y HH:mm:ss' }}</div>
                <div><span class="muted">Last message</span>{{ conversation.lastMessageAt ? (conversation.lastMessageAt | date: 'MMM d, y HH:mm:ss') : '-' }}</div>
                <div><span class="muted">Messages</span>{{ conversation.messageCount }}</div>
                <div><span class="muted">Tokens in / out</span>{{ totals.input | number }} / {{ totals.output | number }}</div>
                <div *ngIf="totals.failed"><span class="muted">Failed answers</span><span class="badge off">{{ totals.failed }}</span></div>
            </div>

            <div class="thread">
                <div *ngIf="conversation && messages.length === 0" class="no-data"><i class="fa-solid fa-comments"></i> No messages</div>
                <div *ngFor="let m of messages" class="msg" [ngClass]="m.role.toLowerCase()">
                    <div class="msg-head">
                        <span class="badge" [ngClass]="m.role === 'USER' ? 'draft' : 'ok'">{{ m.role }}</span>
                        <span class="muted">{{ m.createdAt | date: 'MMM d, y HH:mm:ss' }}</span>
                        <span class="flex-spacer"></span>
                        <span class="muted" *ngIf="m.modelId">{{ m.modelId }}</span>
                    </div>
                    <div class="msg-attachments" *ngIf="attachmentsOf(m).length">
                        <div class="msg-attachment" *ngFor="let a of attachmentsOf(m)" [ngSwitch]="a.kind">
                            <ng-container *ngIf="a.kind === 'DOCUMENT'">
                                <button type="button" class="doc-chip" [disabled]="downloading[a.attachmentId]" (click)="downloadDocument(a)"
                                    [attr.aria-label]="'Download ' + documentName(a)" title="Download the document">
                                    <i class="fa-solid fa-file-lines"></i>
                                    <span class="doc-name">{{ documentName(a) }}</span>
                                    <span class="muted doc-meta">{{ documentMeta(a) }}</span>
                                    <i class="fa-solid fa-download"></i>
                                </button>
                                <span class="error-text" *ngIf="attachmentErrors[a.attachmentId]">
                                    <i class="fa-solid fa-triangle-exclamation"></i> {{ attachmentErrors[a.attachmentId] }}
                                </span>
                            </ng-container>
                            <ng-container *ngIf="a.kind !== 'DOCUMENT'">
                            <ng-container *ngIf="attachmentUrls[a.attachmentId]; else attachmentPending">
                                <a *ngSwitchCase="'IMAGE'" [href]="attachmentUrls[a.attachmentId]" target="_blank" rel="noopener" title="Open the picture">
                                    <img [src]="attachmentUrls[a.attachmentId]" alt="Picture sent in the conversation" class="msg-image" />
                                </a>
                                <audio *ngSwitchCase="'AUDIO'" controls preload="metadata" [src]="attachmentUrls[a.attachmentId]" class="msg-audio"></audio>
                                <span *ngSwitchDefault class="muted">{{ a.kind }} ({{ a.contentType }})</span>
                            </ng-container>
                            <ng-template #attachmentPending>
                                <span class="muted" *ngIf="!attachmentErrors[a.attachmentId]">
                                    <i class="fa-solid" [ngClass]="a.kind === 'AUDIO' ? 'fa-microphone' : 'fa-image'"></i> Loading {{ a.kind === 'AUDIO' ? 'recording' : 'picture' }}...
                                </span>
                                <span class="error-text" *ngIf="attachmentErrors[a.attachmentId]">
                                    <i class="fa-solid fa-triangle-exclamation"></i> {{ attachmentErrors[a.attachmentId] }}
                                </span>
                            </ng-template>
                            </ng-container>
                        </div>
                    </div>
                    <div class="transcript-label muted" *ngIf="isTranscript(m)"><i class="fa-solid fa-microphone"></i> Transcript</div>
                    <div class="msg-body" dir="auto" *ngIf="m.content">{{ m.content }}</div>
                    <div class="msg-playbooks" *ngIf="playbooksOf(m).length">
                        <span class="playbook-chip" *ngFor="let p of playbooksOf(m)" [title]="'Playbook key: ' + p.key" dir="auto">
                            <i class="fa-solid fa-book-open"></i> Playbook: {{ p.title }}
                        </span>
                    </div>
                    <div class="msg-tools" *ngIf="toolsOf(m).length">
                        <span
                            class="badge tool-chip"
                            *ngFor="let t of toolsOf(m)"
                            [ngClass]="t.problem ? 'warn' : 'draft'"
                            [title]="'Service: ' + (t.service || '-') + ' · outcome: ' + (t.outcome || '-')"
                        >
                            <i class="fa-solid fa-screwdriver-wrench"></i> Tool: {{ t.tool }}<span *ngIf="t.outcomeLabel"> · {{ t.outcomeLabel }}</span>
                        </span>
                    </div>
                    <div class="msg-meta" *ngIf="m.role === 'ASSISTANT'">
                        <span>Tokens in {{ m.inputTokens ?? '-' }} / out {{ m.outputTokens ?? '-' }}</span>
                        <span>Latency {{ m.latencyMs !== null && m.latencyMs !== undefined ? (m.latencyMs | number) + ' ms' : '-' }}</span>
                        <span *ngIf="m.finishReason">Finish: {{ m.finishReason }}</span>
                    </div>
                    <div class="msg-error" *ngIf="m.failed || m.error">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        Provider error{{ m.error ? ': ' + m.error : '' }}
                    </div>
                </div>
            </div>
        </div>
    </div>
</section>
